import { Button } from '@/components/Button';
import { Helmet } from 'react-helmet-async';
import ComparisonTable from './component/ComparisonTable';
import FunctionalDescription from './component/FunctionalDescription';
import PlansFaq from './component/PlansFaq';
import { StripeCheckoutSession } from '@/types/stripeCheckoutSession';
import Footer from '@/ui/Footer';
import PlansFirstView from './component/PlansFirstView';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiPostRequest } from '@/utils/apiClient';
import { detectDeviceAndBrowser } from '@/utils/deviceAndBrowser';
import LoadingForStripe from '@/components/LoadingForStripe';
import { basicPlanFunctionalDescription } from '@/constants/basicPlan';
import { useTranslation } from 'react-i18next';

function Plans() {
    const [isFixed, setIsFixed] = useState(true);
    const buttonRef = useRef<HTMLDivElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const [isLoadingForStripe, setIsLoadingForStripe] = useState(false);
    const [sessionContext, setSessionContext] = useState('normal');
    const [searchParams] = useSearchParams();
    const { t, i18n } = useTranslation('plans');

    // クエリパラメータ取得
    useEffect(() => {
        // クエリパラメータからsessionContextを取得
        const sessionContextParam = searchParams.get('sessionContext');
        if (sessionContextParam) {
            setSessionContext(sessionContextParam);

            // URLパラメータを削除（クリーンアップ）
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [searchParams]);

    // メイン処理
    useEffect(() => {
        // トライアルボタンの固定表示用
        const handleScroll = () => {
            if (placeholderRef.current) {
                const placeholderPosition = placeholderRef.current.getBoundingClientRect().top;
                setIsFixed(placeholderPosition > window.innerHeight);
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // 初期状態の設定

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // 無料トライアルボタンクリック時
    const handleClickTrialButton = async () => {
        try {
            // 送信データ作成
            const requestBody: StripeCheckoutSession = {
                planName: 'basic_plan',
                locale: i18n.language.startsWith('ja') ? 'ja' : 'en',
                campaign: 'none',
                sessionContext: sessionContext,
                deviceType: detectDeviceAndBrowser(),
            };
            // チェックアウトAPI
            const response = await apiPostRequest(
                '/api/payment/stripe/create-checkout-session',
                requestBody,
            );
            // 成功時
            if (response?.ok) {
                const responseData = await response.json();
                setIsLoadingForStripe(true);
                // 3秒後にリダイレクト
                setTimeout(() => {
                    window.location.href = responseData.url;
                }, 3000);
                // 400エラー
            } else if (response?.status === 400) {
                const responseData = await response.json();
                const errorList: string[] = Object.values(responseData.errors).flatMap((val) =>
                    Array.isArray(val) ? val : [val],
                );
                // アラート表示
                alert(errorList.join('\n'));
                // その他エラー
            } else {
                // エラー時はログイン画面にリダイレクト
                window.location.replace('/login?error=server_error');
            }
        } catch (error) {
            // エラー時はログイン画面にリダイレクト
            window.location.replace('/login?error=server_error');
        }
    };

    if (isLoadingForStripe) {
        return <LoadingForStripe />;
    }

    return (
        <>
            <Helmet>
                <title>{t('meta.title')}</title>
            </Helmet>

            <main className="min-h-screen max-w-[1440px] mx-auto">
                {/** ファーストビュー */}
                <PlansFirstView />

                <div className="px-1 lg:px-6">
                    {/** 機能説明 */}
                    <FunctionalDescription items={basicPlanFunctionalDescription} />

                    {/** 比較表 */}
                    <h2 className="text-center text-3xl lg:text-5xl font-bold tracking-widest mt-16 lg:mt-32 text-gray-900">
                        {t('comparison.heading')}
                    </h2>
                    <ComparisonTable />

                    {/** よくある質問 */}
                    <h2 className="text-center text-3xl lg:text-5xl font-bold tracking-widest mt-16 lg:mt-32 text-gray-900">
                        {t('faq.heading')}
                    </h2>
                    <PlansFaq />

                    {/** トライアル */}
                    <h2 className="text-center text-3xl lg:text-5xl font-bold leading-relaxed tracking-widest mt-12 lg:mt-20 text-gray-900">
                        {t('trial.heading')}
                    </h2>
                    <div className="w-full lg:w-1/2 flex flex-col items-center justify-center text-lg lg:text-2xl tracking-widest mx-auto px-6 lg:px-0 leading-loose text-center text-gray-700">
                        <p
                            className="mt-6 lg:mt-20"
                            dangerouslySetInnerHTML={{
                                __html: t('trial.lead1', { interpolation: { escapeValue: false } }),
                            }}
                        />
                        <p
                            className="mt-6 lg:mt-10"
                            dangerouslySetInnerHTML={{
                                __html: t('trial.lead2', { interpolation: { escapeValue: false } }),
                            }}
                        />
                    </div>

                    {/** 登録ボタン */}
                    {/* プレースホルダー：本来のボタンの位置 */}
                    <div
                        ref={placeholderRef}
                        className="flex justify-center pt-16 pb-24 lg:pt-24 lg:pb-36"
                    >
                        {!isFixed && (
                            <div className="flex flex-col items-center justify-center">
                                <Button
                                    variant="original"
                                    className="w-auto h-16 lg:h-20 border-2 border-gray-800 shadow-md"
                                    onClick={handleClickTrialButton}
                                >
                                    <span className="text-xl lg:text-2xl tracking-[0.2em]">
                                        {t('cta.button')}
                                    </span>
                                </Button>
                                <span className="mt-3 text-xs text-gray-500 tracking-[0.2em]">
                                    {t('cta.note')}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* 固定表示されるボタン */}
                    {isFixed && (
                        <div
                            ref={buttonRef}
                            className="flex flex-col items-center justify-center fixed max-w-[1440px] mx-auto bg-gray-50 bottom-0 w-full z-50 py-10"
                        >
                            <Button
                                variant="original"
                                className="w-auto h-16 lg:h-20 border-2 border-gray-800 shadow-md"
                                onClick={handleClickTrialButton}
                            >
                                <span className="text-xl lg:text-2xl tracking-[0.2em]">
                                    {t('cta.button')}
                                </span>
                            </Button>
                            <span className="mt-3 text-xs text-gray-500 tracking-[0.2em]">
                                {t('cta.note')}
                            </span>
                        </div>
                    )}
                </div>
            </main>

            {/* フッター */}
            <Footer />
        </>
    );
}

export default Plans;
